import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Banner {
  id: number;
  title: string;
  subtitle: string;
  gradient: string;
}

const banners: Banner[] = [
  {
    id: 1,
    title: "NEPAL'S #1 ONLINE MARKETPLACE",
    subtitle: 'Buy. Sell. Discover amazing deals.',
    gradient: 'linear-gradient(135deg, #ff9a7a 0%, var(--pw-primary) 50%, #ffbf4d 100%)',
  },
  {
    id: 2,
    title: 'THOUSANDS OF LISTINGS',
    subtitle: 'Find what you need, sell what you don\'t.',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #ec4899 100%)',
  },
  {
    id: 3,
    title: 'FAST & EASY',
    subtitle: 'Post your ad in minutes.',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #6366f1 100%)',
  },
];

const MobileBanner: React.FC = () => {
  const [currentBanner, setCurrentBanner] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  };

  return (
    <div className="position-relative overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentBanner}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5 }}
          style={{
            height: 180,
            background: banners[currentBanner].gradient,
            padding: 24,
            position: 'relative',
          }}
        >
          <div
            className="position-relative h-100 d-flex flex-column justify-content-center"
            style={{ zIndex: 10 }}
          >
            <h2
              className="fw-bold mb-2"
              style={{
                color: 'white',
                fontSize: 22,
                lineHeight: 1.25,
                textShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              {banners[currentBanner].title}
            </h2>
            <p
              className="mb-0"
              style={{
                color: 'rgba(255,255,255,0.92)',
                fontSize: 15,
                fontWeight: 500,
                textShadow: '0 1px 4px rgba(0,0,0,0.1)',
              }}
            >
              {banners[currentBanner].subtitle}
            </p>
          </div>

          {/* Decorative background elements */}
          <div
            className="position-absolute"
            style={{
              top: -40,
              right: -40,
              width: 160,
              height: 160,
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              filter: 'blur(40px)',
            }}
          />
          <div
            className="position-absolute"
            style={{
              bottom: -30,
              left: -30,
              width: 120,
              height: 120,
              background: 'rgba(255,255,255,0.08)',
              borderRadius: '50%',
              filter: 'blur(30px)',
            }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      <button
        onClick={prevBanner}
        className="position-absolute border-0 d-flex align-items-center justify-content-center"
        style={{
          left: 10,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(4px)',
          color: 'white',
          cursor: 'pointer',
          transition: 'background 0.15s ease',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
      >
        <svg
          style={{ width: 16, height: 16 }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={nextBanner}
        className="position-absolute border-0 d-flex align-items-center justify-content-center"
        style={{
          right: 10,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(4px)',
          color: 'white',
          cursor: 'pointer',
          transition: 'background 0.15s ease',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
      >
        <svg
          style={{ width: 16, height: 16 }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Pagination Dots */}
      <div
        className="position-absolute d-flex gap-2"
        style={{
          left: '50%',
          bottom: 12,
          transform: 'translateX(-50%)',
        }}
      >
        {banners.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentBanner(index)}
            className="border-0 p-0"
            style={{
              height: 6,
              borderRadius: 999,
              background: index === currentBanner ? 'white' : 'rgba(255,255,255,0.5)',
              width: index === currentBanner ? 24 : 6,
              transition: 'all 0.3s ease',
              cursor: 'pointer',
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default MobileBanner;
